import { differenceInDays } from "date-fns"
import { countBy, sortBy, uniq } from "lodash-es"
import { ExternalLink } from "lucide-react"
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react"

import type { CompanyRunResearch } from "@/admin/monitoring/company-run-research-data"
import {
  dataByResearchMark,
  type ResearchMark,
} from "@/admin/monitoring/research-mark"
import {
  formatRunStart,
  humanizeKey,
  pluralize,
} from "@/admin/monitoring/run-presentation"
import { SourceArtifactViewer } from "@/admin/monitoring/source-artifact-viewer"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { DueReason } from "@/constants/terminal/due-reason"
import { ExecutionOutcome } from "@/constants/terminal/execution-outcome"
import { dataBySourceCheckOutcome } from "@/constants/terminal/source-check-outcome"
import { cn } from "@/lib/utils"
import { sourcePlatformDisplayName } from "@/terminal/research/platform-display-names"

type Cell = CompanyRunResearch["bands"][number]["cells"][number]

// A mark that changes on a poll eases into its new fill rather than
// snapping, so a table filling in reads as motion, not flicker.
export const ResearchMarkSquare = ({
  mark,
  className,
}: {
  mark: ResearchMark
  className?: string
}) => (
  <span
    aria-hidden
    className={cn(
      "inline-block size-2.5 shrink-0 transition-colors duration-500 motion-reduce:transition-none",
      dataByResearchMark[mark].className,
      className
    )}
  />
)

// The marks on screen, worst first, each with how many cells carry it.
export const ResearchMarkLegend = ({
  marks,
  className,
  children,
}: {
  marks: ResearchMark[]
  className?: string
  children?: ReactNode
}) => {
  const countByMark = countBy(marks)
  return (
    <ul
      className={cn(
        "text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs",
        className
      )}
    >
      {sortBy(uniq(marks), (mark) => dataByResearchMark[mark].severity).map(
        (mark) => (
          <li key={mark} className="flex items-center gap-1.5">
            <ResearchMarkSquare mark={mark} />
            {dataByResearchMark[mark].label}
            <span className="tabular-nums">{countByMark[mark]}</span>
          </li>
        )
      )}
      {children}
    </ul>
  )
}

// The Tasks whose mark differs from the previous poll's, highlighted until
// the next one, so a refresh shows what moved. Switching category starts afresh.
const useChangedTaskIds = (cells: Cell[]) => {
  const previousMarkByTaskId = useRef<Map<string, ResearchMark> | null>(null)
  const [changedTaskIds, setChangedTaskIds] = useState<Set<string>>(
    () => new Set()
  )
  useEffect(() => {
    const markByTaskId = new Map(
      cells.flatMap((cell) =>
        cell.researchTaskId ? [[cell.researchTaskId, cell.mark] as const] : []
      )
    )
    const previous = previousMarkByTaskId.current
    previousMarkByTaskId.current = markByTaskId
    setChangedTaskIds(
      new Set(
        [...markByTaskId].flatMap(([id, mark]) =>
          previous?.has(id) && previous.get(id) !== mark ? [id] : []
        )
      )
    )
  }, [cells])
  return changedTaskIds
}

// A share of a whole as a short bar, the console's one in-row gauge.
export const ShareBar = ({
  share,
  fillClassName = "bg-muted-foreground/60",
  className,
}: {
  share: number
  fillClassName?: string
  className?: string
}) => (
  <span className={cn("bg-border-faint block h-1 w-16", className)}>
    <span
      className={cn("block h-full", fillClassName)}
      style={{ width: `${share * 100}%` }}
    />
  </span>
)

// What the search found, in words: a discovered listing by the name and city
// its own profile record carries, else the Source's key.
export const sourceDescription = (cell: Cell) =>
  cell.profileLabel ?? humanizeKey(cell.sourceKey)

// Filters by outcome, worst first, once a table has more than one.
const OutcomeFilter = ({
  cells,
  selected,
  onSelect,
}: {
  cells: Cell[]
  selected: ResearchMark | null
  onSelect: (mark: ResearchMark | null) => void
}) => {
  const countByMark = countBy(cells, "mark")
  const marks = sortBy(
    Object.keys(countByMark) as ResearchMark[],
    (mark) => dataByResearchMark[mark].severity
  )
  if (marks.length < 2) return null
  const chip = (mark: ResearchMark | null, label: string, count: number) => (
    <button
      key={mark ?? "all"}
      aria-pressed={selected === mark}
      onClick={() => onSelect(mark)}
      className={cn(
        "focus-visible:ring-terminal-accent flex items-center gap-1.5 rounded-sm border px-2 py-1 focus-visible:ring-1 focus-visible:outline-none",
        "hover:bg-surface-hover",
        selected === mark ? "border-foreground/60" : "text-muted-foreground"
      )}
    >
      {mark && <ResearchMarkSquare mark={mark} />}
      {label}
      <span className="tabular-nums">{count}</span>
    </button>
  )
  return (
    <div
      role="group"
      aria-label="Filter Research Tasks by outcome"
      className="flex flex-wrap gap-1.5 text-xs"
    >
      {chip(null, "All", cells.length)}
      {marks.map((mark) =>
        chip(mark, dataByResearchMark[mark].label, countByMark[mark] ?? 0)
      )}
    </div>
  )
}

// Why a Task is due, or when it next is, as of the run's own moment
// (ADR-0176): a retry waits on its own time, a stale answer on its age.
const dueLabel = (due: NonNullable<Cell["due"]>, asOf: string) => {
  const { isDue } = due
  switch (due.reason) {
    case DueReason.NEVER_CHECKED:
      return "Never checked"
    case DueReason.DEFERRED:
      return isDue ? "Deferred from an earlier run" : "Deferred to a later run"
    case DueReason.RETRY:
      return `Retry due ${formatRunStart(due.dueAt)}${due.errorMessage ? ` · ${due.errorMessage}` : ""}`
    case DueReason.STALE: {
      const days = differenceInDays(asOf, due.checkedAt ?? asOf)
      const read =
        days < 1 ? "read today" : `last read ${pluralize(days, "day")} ago`
      return isDue
        ? `Stale · ${read}`
        : `Due again ${formatRunStart(due.dueAt)} · ${read}`
    }
  }
}

// The full label stays on hover once a long error message is clamped.
const DueLine = ({ label }: { label: string }) => (
  <p className="text-muted-foreground mt-0.5 line-clamp-2" title={label}>
    {label}
  </p>
)

// Consecutive rows searching one Source in one place share one bordered
// group under a shaded header row, so a long Source name spans the table
// instead of wrapping.
const isSameSource = (other: Cell, cell: Cell) =>
  other.platformKey === cell.platformKey &&
  sourceDescription(other) === sourceDescription(cell) &&
  other.locator === cell.locator &&
  other.geographyLabel === cell.geographyLabel

const SourceHeaderRow = ({
  cell,
  isDescriptionShared,
}: {
  cell: Cell
  isDescriptionShared: boolean
}) => {
  const platformName = sourcePlatformDisplayName(cell.platformKey)
  return (
    <TableRow className="bg-muted/60 hover:bg-muted/60">
      <TableCell colSpan={5} className="py-2!">
        <div className="flex items-center justify-between gap-4">
          <span
            className="flex min-w-0 items-center gap-1.5"
            title={`${cell.platformKey} ${cell.sourceKey}`}
          >
            <span className="truncate font-medium">{platformName}</span>
            {!isDescriptionShared && (
              <span className="text-muted-foreground truncate">
                {sourceDescription(cell)}
              </span>
            )}
            {cell.locator && (
              <a
                href={cell.locator}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${platformName} listing`}
                className="text-muted-foreground hover:text-foreground shrink-0"
              >
                <ExternalLink className="size-3" />
              </a>
            )}
          </span>
          {cell.geographyLabel && (
            <span className="text-muted-foreground shrink-0">
              {cell.geographyLabel}
            </span>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

// The row's mark is the keyboard way to open its Source Check, since the
// Source's name sits in the group's header row.
const SubjectCell = ({
  cell,
  subjectName,
  onOpenTask,
}: {
  cell: Cell
  subjectName: string
  onOpenTask: (id: string) => void
}) => {
  const { researchTaskId } = cell
  const mark = (
    <ResearchMarkSquare
      mark={cell.mark}
      className={cn(!cell.isWeighed && "opacity-40")}
    />
  )
  return (
    <TableCell className="min-w-48">
      <div className="flex gap-2.5">
        {/* One text-xs line tall, so the mark sits on line one. */}
        {researchTaskId ? (
          <button
            className="focus-visible:ring-terminal-accent flex h-4 items-center focus-visible:ring-1 focus-visible:outline-none"
            data-research-task-id={researchTaskId}
            aria-label={`Open the ${sourcePlatformDisplayName(cell.platformKey)} Source Check`}
            title="Open the Source Check"
            onClick={() => onOpenTask(researchTaskId)}
          >
            {mark}
          </button>
        ) : (
          <span className="flex h-4 items-center">{mark}</span>
        )}
        <span>
          {subjectName}
          {cell.isOptional && (
            <span className="text-muted-foreground block">Optional</span>
          )}
        </span>
      </div>
    </TableCell>
  )
}

export const ResearchSourceTable = ({
  research,
  cells,
  onOpenTask,
}: {
  research: CompanyRunResearch
  cells: Cell[]
  onOpenTask: (id: string) => void
}) => {
  const [markFilter, setMarkFilter] = useState<ResearchMark | null>(null)
  const changedTaskIds = useChangedTaskIds(cells)
  const shownCells = cells.filter(
    (cell) => markFilter === null || cell.mark === markFilter
  )
  const maxRecordCount = Math.max(1, ...cells.map((cell) => cell.recordCount))
  // One search kind on every row says nothing per row.
  const isDescriptionShared = uniq(cells.map(sourceDescription)).length === 1
  const sourceGroups = shownCells.reduce<Cell[][]>((groups, cell) => {
    const lastGroup = groups.at(-1)
    if (lastGroup && isSameSource(lastGroup[0], cell)) lastGroup.push(cell)
    else groups.push([cell])
    return groups
  }, [])
  return (
    <div className="space-y-3">
      <OutcomeFilter
        cells={cells}
        selected={markFilter}
        onSelect={setMarkFilter}
      />
      <Table className="min-w-2xl text-xs [&_td]:px-3 [&_td]:py-3 [&_td]:align-top [&_th]:px-3 [&_th]:text-xs [&_th]:whitespace-nowrap">
        <TableHeader>
          <TableRow>
            <TableHead>Search Subject</TableHead>
            <TableHead title="Query Variant">Name searched</TableHead>
            <TableHead>Outcome</TableHead>
            <TableHead className="text-right">Records</TableHead>
            <TableHead>Artifacts</TableHead>
          </TableRow>
        </TableHeader>
        {sourceGroups.map((group, groupIndex) => (
          <Fragment
            key={`${group[0].researchTaskId ?? group[0].sourceKey}:${groupIndex}`}
          >
            {groupIndex > 0 && (
              <tbody aria-hidden>
                <tr>
                  <td colSpan={5} className="h-3 p-0!" />
                </tr>
              </tbody>
            )}
            <TableBody className="border">
              <SourceHeaderRow
                cell={group[0]}
                isDescriptionShared={isDescriptionShared}
              />
              {group.map((cell, index) => {
                const platformName = sourcePlatformDisplayName(cell.platformKey)
                const { researchTaskId } = cell
                return (
                  <TableRow
                    key={`${cell.researchTaskId ?? cell.sourceKey}:${index}`}
                    className={cn(
                      researchTaskId && "cursor-pointer",
                      researchTaskId &&
                        changedTaskIds.has(researchTaskId) &&
                        "bg-terminal-accent/10"
                    )}
                    // The whole row opens its Source Check; its own links and
                    // buttons keep their own action.
                    onClick={(event) => {
                      if (
                        researchTaskId &&
                        !(event.target as HTMLElement).closest("a, button")
                      )
                        onOpenTask(researchTaskId)
                    }}
                  >
                    <SubjectCell
                      cell={cell}
                      subjectName={
                        research.subjectNameByKey[cell.subjectKey] ??
                        cell.subjectKey
                      }
                      onOpenTask={onOpenTask}
                    />
                    <TableCell
                      className={cn(
                        "min-w-24",
                        !cell.queryVariant && "text-muted-foreground"
                      )}
                    >
                      {cell.queryVariant ?? "Own name"}
                    </TableCell>
                    <TableCell className="min-w-36">
                      {cell.checkOutcome
                        ? dataBySourceCheckOutcome[cell.checkOutcome].label
                        : dataByResearchMark[cell.mark].label}
                      {cell.extractionOutcome === ExecutionOutcome.FAILED && (
                        <p className="text-assessment-critical mt-0.5">
                          Extraction failed
                        </p>
                      )}
                      {cell.due && (
                        <DueLine label={dueLabel(cell.due, research.asOf)} />
                      )}
                    </TableCell>
                    <TableCell className="min-w-24 text-right tabular-nums">
                      {cell.recordCount}
                      {/* What the Assessment attributed to the Company; an
                          earlier Source Check may supply it. */}
                      {cell.inScopeRecordCount !== null &&
                        cell.recordCount + cell.inScopeRecordCount > 0 && (
                          <span className="text-muted-foreground block whitespace-nowrap">
                            {cell.inScopeRecordCount} in scope
                          </span>
                        )}
                      {/* Where the evidence comes from, at a glance. */}
                      <ShareBar
                        share={cell.recordCount / maxRecordCount}
                        className="mt-1 ml-auto"
                      />
                    </TableCell>
                    <TableCell className="min-w-32">
                      {cell.sourceArtifacts.length === 0 ? (
                        <span className="text-muted-foreground">–</span>
                      ) : (
                        cell.sourceArtifacts.map((artifact) => {
                          const role = humanizeKey(artifact.role)
                          return (
                            <SourceArtifactViewer
                              key={artifact.privateObjectPath}
                              artifact={{
                                ...artifact,
                                label: `${platformName}, ${role}`,
                              }}
                              triggerLabel={role}
                            />
                          )
                        })
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Fragment>
        ))}
      </Table>
    </div>
  )
}
